import { Link } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { PublicLayout } from '@/components/PublicLayout'
import { GradientBorderCard, ContentCard, CardWithButtons } from '@/components/GradientBorderCard'

// Cache-busting: update this version when you change images
const IMAGE_VERSION = 'v2'

// Helper to add cache-busting query param to image URLs
const withCacheBust = (path: string) => `${path}?${IMAGE_VERSION}`


import { 
  BookOpen, 
  PenTool,
  Compass,
  Lightbulb,
  ChevronRight,
  Shield,
  Zap,
  Users
} from 'lucide-react'

interface AboutProps {
  user?: {
    id: number
    name: string
    email: string
    username?: string
    role?: 'user' | 'admin' | 'manager'
  }
}

// ═══════════════════════════════════════════════════════════════
// BACKGROUND IMAGE CONFIGURATION
// ═══════════════════════════════════════════════════════════════
const CONFIG = {
  enabled: true,                     // Toggle background on/off
  image: withCacheBust('/images/two-cricles.webp'),  // Image file (in /public/images)
  opacity: 0.3,                     // Opacity 0-1
  width: 50,                         // Width as percentage (10-100)
  rotation: 90,                      // Rotation in degrees (0, 90, 180, 270)
  repeat: 'no-repeat',               // Background repeat style
  position: 'center center',         // Background position
}
// ═══════════════════════════════════════════════════════════════

export default function About({ user }: AboutProps) {
  return (
    <PublicLayout user={user}>
      {/* FIXED BACKGROUND - sits behind all content, stays in viewport */}
      {CONFIG.enabled && (
        <div 
          className="fixed inset-0 z-0 pointer-events-none"
          style={{
            backgroundImage: `url(${CONFIG.image})`,
            backgroundRepeat: CONFIG.repeat,
            backgroundPosition: CONFIG.position,
            backgroundSize: 'cover',
            transform: `rotate(${CONFIG.rotation}deg)`,
            opacity: CONFIG.opacity,
            width: `${CONFIG.width}%`,
            left: `${(100 - CONFIG.width) / 2}%`,
            top: 0,
            bottom: 0,
          }}
        />
      )}

      {/* CONTENT - all sections overlay the background */}
      <main className="relative z-10">
        {/* Hero Section */}
        <section className="py-32 min-h-[500px] flex items-center">
          <div className="w-full px-4">
            <GradientBorderCard maxWidth="3xl" className="mx-auto">
              <h1 className="text-4xl md:text-5xl font-semibold text-foreground mb-6">
                About MantraVid
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Building a home for the mindful technologist. We explore the intersection 
                where deep technical knowledge meets conscious exploration.
              </p>
            </GradientBorderCard>
          </div>
        </section>

        <Separator className="container mx-auto" />

        {/* Our Mission */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <ContentCard
              icon={<PenTool className="w-8 h-8 text-primary" />}
              iconBgGradient="primary"
              title="Our Mission"
              maxWidth="3xl"
              centered
            >
              <p className="text-muted-foreground text-lg leading-relaxed">
                We believe in the power of technology to transform how people connect, learn, and grow. 
                Our platform is designed with one goal in mind: to provide a seamless, intuitive 
                experience that helps our users accomplish their goals efficiently and enjoyably.
                Behind every line of code lies clarity, creativity, and intention—we see code not 
                merely as mechanics, but as a form of consciousness exploration.
              </p>
            </ContentCard>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <GradientBorderCard maxWidth="5xl" className="mb-12">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500/10 to-primary/10 mb-6">
                  <Compass className="w-8 h-8 text-purple-500" />
                </div>
                <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4">
                  Our Values
                </h2>
                <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                  The principles that guide everything we build
                </p>
              </div>
            </GradientBorderCard>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Card className="group hover:border-primary/30 transition-colors bg-black/60">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Trust & Security</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    Your privacy and security are our top priorities. We employ industry-leading 
                    practices to keep your data safe. We believe in building systems that respect 
                    user autonomy.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="group hover:border-primary/30 transition-colors bg-black/60">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                      <Zap className="w-5 h-5 text-purple-500" />
                    </div>
                    <CardTitle className="text-xl">Innovation</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    We continuously improve our platform, adding new features and enhancements 
                    based on user feedback and emerging technologies. We embrace thoughtful 
                    experimentation.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="group hover:border-primary/30 transition-colors bg-black/60">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                      <Users className="w-5 h-5 text-emerald-500" />
                    </div>
                    <CardTitle className="text-xl">Community</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    We're building more than a platform—we're creating a community of users 
                    who support and inspire each other. Together, we explore the frontiers 
                    of mindful technology.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <Separator className="container mx-auto" />

        {/* Our Story */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <ContentCard
              icon={<Lightbulb className="w-8 h-8 text-purple-500" />}
              iconBgGradient="purple"
              title="Our Story"
              maxWidth="3xl"
              centered={false}
            >
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Founded with a vision to simplify complex workflows, we started as a small team 
                with big ideas. Through dedication and close collaboration with our users, 
                we've evolved into the platform you see today.
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                Every feature we build, every update we release, is driven by our commitment 
                to your success. We listen to your feedback, analyze your needs, and work 
                tirelessly to deliver solutions that make a real difference. This space is 
                dedicated to exploring how deep technical knowledge and mindful practice 
                can transform how we build with AI.
              </p>
              <Button variant="outline" size="lg" asChild>
                <Link href="/blog">
                  <BookOpen className="mr-2 w-4 h-4" />
                  Read Our Latest Articles
                </Link>
              </Button>
            </ContentCard>
          </div>
        </section>

        {/* Join Our Journey */}
        <section className="py-32 min-h-[500px] flex items-center">
          <div className="w-full px-4">
            <CardWithButtons
              title="Join Our Journey"
              description={
                "Whether you're a longtime user or just discovering us, we're excited to have " +
                "you be part of our story. Get started today and experience the difference. " +
                "Your awareness is our algorithm."
              }
              primaryButton={
                user 
                  ? { text: 'Go to Your Dashboard', href: '/dashboard' }
                  : { text: 'Create Free Account', href: '/sign_up' }
              }
              secondaryButton={
                user ? undefined : { text: 'Read Our Blog', href: '/blog' }
              }
              maxWidth="3xl"
            />
          </div>
        </section>
      </main>
    </PublicLayout>
  )
}
